import { GlowButton } from "@components/atoms/GlowButton";
import { DbSolanaNftCollection, NftCollection, NftTypes } from "@glow/shared";
import { LuxInputLabel } from "@lux/atoms/components/LuxInputLabel";
import { LuxMenu } from "@lux/atoms/components/LuxMenu";
import { useWindowSize } from "@lux/atoms/hooks/useWindowSize";
import { ResponsiveBreakpointNumbers } from "@lux/atoms/utils/StyleConstants";
import FilterIcon from "@lux/icons/feather/filter.svg";
import { ResponsiveBreakpoint } from "@utils/StyleConstants";
import React, { useEffect, useState } from "react";

export const CollectionFilters = ({
  traitsSummary,
  traitFilters,
  setTraitFilters,
}: {
  traitsSummary: DbSolanaNftCollection["traits_summary"];
  traitFilters: NftTypes.NftTrait[];
  setTraitFilters: (traitFilters: NftTypes.NftTrait[]) => void;
}) => {
  const toggleFilter = ({
    trait_type,
    value,
  }: {
    trait_type: string;
    value: NftTypes.NftTraitValue | null;
  }) => {
    let newFilters = [...traitFilters];

    if (value == null) {
      // We are unsetting the filter
      newFilters = newFilters.filter(({ trait_type: tt }) => tt !== trait_type);
    } else {
      const existingIndex = newFilters.findIndex(
        (filter) => filter.trait_type === trait_type,
      );
      if (existingIndex !== -1) {
        newFilters[existingIndex] = { trait_type, value };
      } else {
        newFilters.push({ trait_type, value });
      }
    }

    setTraitFilters(newFilters);
  };

  return (
    <div className="collection-filters">
      <div className="flex-baseline spread title">
        <h3>Filters</h3>

        {traitFilters.length > 0 && (
          <GlowButton
            label="Clear All"
            variant="link"
            color="secondary"
            onClick={() => setTraitFilters([])}
          />
        )}
      </div>
      <div className="traits">
        {traitsSummary?.map(({ trait_type, values }, idx) => (
          <CollectionFilter
            key={idx}
            trait_type={trait_type}
            values={values}
            activeValue={
              traitFilters.find((filter) => filter.trait_type === trait_type)
                ?.value || null
            }
            toggleFilter={toggleFilter}
          />
        ))}
      </div>

      <style jsx>{`
        .collection-filters {
          background-color: var(--primary-bg-color);

          :global(.luma-button) {
            box-shadow: none !important;
          }

          .title {
            padding-bottom: 1.5rem;

            h3 {
              margin: 0;
            }
          }

          @media (max-width: ${ResponsiveBreakpoint.medium}) {
            padding-top: 0.5rem;

            .title {
              display: none;
            }

            .traits {
              padding: 0.5rem var(--side-padding);
              padding-bottom: 0;
              display: flex;
              overflow: auto;
            }
          }
        }
      `}</style>
    </div>
  );
};

export const CollectionFilter = ({
  trait_type,
  values,
  activeValue,
  toggleFilter,
}: NftCollection.TraitSummary & {
  activeValue: any | null;
  toggleFilter: (value: {
    trait_type: string;
    value: NftTypes.NftTraitValue | null;
  }) => void;
}) => {
  const [open, setOpen] = useState(false);
  const { width } = useWindowSize();
  const [label, setLabel] = useState("Show All");

  useEffect(() => {
    if (width && width < ResponsiveBreakpointNumbers.medium) {
      setLabel(trait_type);
    }
  }, [width, setLabel, trait_type]);

  return (
    <div className="filter">
      <LuxInputLabel className="filter-label" text={trait_type} />
      <LuxMenu
        trigger={
          <GlowButton
            label={activeValue || label}
            icon={<FilterIcon />}
            color={activeValue != null ? "primary" : "light"}
            size="small"
            rounded
            className="filter-button"
          />
        }
        open={open}
        setOpen={setOpen}
        placement="bottom-start"
        defaultSelectFirst={false}
        rows={[
          {
            key: "__all",
            name: "Show All",
            onClick: () => toggleFilter({ trait_type, value: null }),
          },
        ].concat(
          values.map(({ value, count }) => ({
            key: JSON.stringify(value),
            name: value.toString(),
            rightText: count,
            value,
            onClick: () => {
              toggleFilter({ trait_type, value });
            },
          })),
        )}
      />

      <style jsx>{`
        .filter {
          max-width: var(--sidebar-width);

          :global(.filter-button) {
            margin-bottom: 1rem;
          }

          :global(.filter-button div) {
            max-width: calc(var(--sidebar-width) - 2rem);
            overflow: hidden;
            text-overflow: ellipsis;
          }

          @media (max-width: ${ResponsiveBreakpoint.medium}) {
            max-width: none;
            padding-right: 0.75rem;

            &:last-child {
              padding-right: 0;
            }

            :global(.filter-label) {
              display: none;
            }

            :global(.filter-button div) {
              max-width: none;
            }
          }

          :global(.filter-button svg) {
            color: var(--tertiary-color);
          }
        }
      `}</style>
    </div>
  );
};
